import { AccessibilityNewRounded as AccessibilityNewRoundedIcon } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';

import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

const WOMEN_COLOR = ColorsRaw.parityBar.Women;
const OTHER_COLOR = ColorsRaw.parityBar.Other;
const MEN_COLOR = ColorsRaw.parityBar.Men;

const BAR_HEIGHT = 36;

export function ParityBarInline({
  women,
  men,
  other,
}: {
  women: number;
  men: number;
  other: number;
}) {
  const total = women + men + other;

  if (total === 0) return null;

  const womenPercent = Math.max((women / total) * 100, 0.5);
  const otherPercent = Math.max((other / total) * 100, 0.5);
  const menPercent = Math.max((men / total) * 100, 0.5);

  return (
    <Box sx={{ minWidth: 300 }}>
      <BarWithIcon
        womenPercent={womenPercent}
        otherPercent={otherPercent}
        menPercent={menPercent}
      />
      <BarLegend women={women} other={other} men={men} />
    </Box>
  );
}

function BarWithIcon({
  womenPercent,
  otherPercent,
  menPercent,
}: {
  womenPercent: number;
  otherPercent: number;
  menPercent: number;
}) {
  return (
    <Stack
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        pl: 0.75,
        backgroundColor: ColorsRaw.anthracite.Warm_Anthracite_2,
        borderRadius: 2,
      }}
    >
      <AccessibilityNewRoundedIcon />
      <BarSegments
        womenPercent={womenPercent}
        otherPercent={otherPercent}
        menPercent={menPercent}
      />
    </Stack>
  );
}

function BarSegments({
  womenPercent,
  otherPercent,
  menPercent,
}: {
  womenPercent: number;
  otherPercent: number;
  menPercent: number;
}) {
  return (
    <Stack
      sx={{ flexDirection: 'row', height: BAR_HEIGHT, gap: 0.25, flex: 1 }}
    >
      <Box
        sx={{
          width: `${womenPercent}%`,
          backgroundColor: WOMEN_COLOR,
          borderRadius: '8px 0 0 8px',
        }}
      />
      <Box sx={{ width: `${otherPercent}%`, backgroundColor: OTHER_COLOR }} />
      <Box
        sx={{
          width: `${menPercent}%`,
          backgroundColor: MEN_COLOR,
          borderRadius: '0 8px 8px 0',
        }}
      />
    </Stack>
  );
}

function BarLegend({
  women,
  other,
  men,
}: {
  women: number;
  other: number;
  men: number;
}) {
  return (
    <Stack sx={{ flexDirection: 'row', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
      <LegendDot
        color={WOMEN_COLOR}
        count={women}
        label={translate('Common.women')}
      />
      <LegendDot
        color={OTHER_COLOR}
        count={other}
        label={translate('Common.other')}
      />
      <LegendDot
        color={MEN_COLOR}
        count={men}
        label={translate('Common.men')}
      />
    </Stack>
  );
}

function LegendDot({
  color,
  count,
  label,
}: {
  color: string;
  count: number;
  label: string;
}) {
  return (
    <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
      <ColorDot color={color} />
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: ColorsRaw.basic.Cream,
          fontWeight: 600,
        }}
      >
        {count}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: ColorsRaw.anthracite.Anthracite_5,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

function ColorDot({ color }: { color: string }) {
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  );
}
